import {zodResolver} from '@hookform/resolvers/zod';
import {Button, Stack, TextField, Typography} from '@mui/material';
import {translations} from 'presentation/translations-new';
import React, {useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';

import {AlertInfo} from 'presentation/components/AlertInfo';
import {Loading} from 'presentation/components/Loading/Loading';
import {useChangeDataFormStateStore} from 'presentation/pages/private/profile/store/changeDataFormStateStore';
import {DiscardChangesModal} from '../DiscardChangesModal';
import {ContactFormProps} from './types';
import {EmailSchema, EmailSchemaFormValues} from './validation';
import {usePersonalData} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {useChangeDataAlertStore} from 'presentation/pages/private/profile/store/changeDataAlertStore';
import {AlertEnum} from 'presentation/pages/private/profile/store/changeDataAlertStore/types';
import {emailRegexValidation, FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {cancel, save, requiredField} = translations['pt-br'].commons;
const {contact, successAlert, errorAlert} = translations['pt-br'].privateFlow.profile.changeData;

const getSuccessMessage = (email?: string) =>
	email
		? `Seu novo e-mail ${email} foi salvo com sucesso. Usaremos ele para enviar avisos do seu plano.`
		: successAlert.account;

const EmailForm = ({
	onCancel,
	onSave,
	resetAbandonError,
	loading,
	abandonError,
}: ContactFormProps) => {
	const [isDiscardChangesModalOpenState, setIsDiscardChangesModalOpenState] = useState(false);
	const setChangeDataFormIsDirtyState = useChangeDataFormStateStore(
		state => state.updateFormState,
	);
	const changeDataAlertState = useChangeDataAlertStore(state => state.status);
	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const {currentEmail} = usePersonalData();
	const {isHolder} = useAccountInformation();
	const currentEmailIsInvalid =
		Boolean(currentEmail) && !emailRegexValidation.test(currentEmail || '');
	const showAlert = changeDataAlertState.status && changeDataAlertState.type === AlertEnum.EMAIL;
	const {typeMessage} = changeDataAlertState;
	const message = {
		success: getSuccessMessage(currentEmail),
		error: errorAlert,
	};

	const SHOW_INFO_ALERT =
		isNewProfileFeatureFlagEnabled && !showAlert && !currentEmailIsInvalid && isHolder;

	const methods = useForm<EmailSchemaFormValues>({
		resolver: zodResolver(EmailSchema),
		mode: 'onSubmit',
		defaultValues: {
			email: isNewProfileFeatureFlagEnabled && currentEmail ? currentEmail : '',
		},
	});

	const {
		register,
		formState: {errors, isDirty},
		handleSubmit,
		watch,
		setError,
	} = methods;

	const handleBack = () => {
		if (!isNewProfileFeatureFlagEnabled && isDirty) {
			setIsDiscardChangesModalOpenState(true);
			return;
		}
		onCancel();
	};

	useEffect(() => {
		setChangeDataFormIsDirtyState({
			isFormDirty: isDirty,
		});
	}, [isDirty]);

	const onSubmit = (data: EmailSchemaFormValues) => {
		if (data.email.trim().toLowerCase() === currentEmail?.trim().toLowerCase()) {
			setError('email', {
				type: 'manual',
				message: contact.sameEmail,
			});
			return;
		}

		onSave(data.email);
	};

	const handleSave = () => {
		if (watch('email').trim().toLowerCase() === currentEmail?.trim().toLowerCase()) {
			setError('email', {
				type: 'manual',
				message: contact.sameEmail,
			});
			return;
		}

		handleSubmit(onSubmit)();
	};

	const handleHelperEmailText = () => {
		const {email} = watch();
		if (errors.email?.message) {
			if (email.length === 0) {
				return requiredField;
			}
			return errors.email.message;
		}
		if (abandonError) {
			return contact.emailAbandonError;
		}
		return undefined;
	};

	if (abandonError) {
		setTimeout(() => {
			resetAbandonError();
		}, 3000);
	}

	return (
		<FormProvider {...methods}>
			<Stack spacing={2} mt={!isNewProfileFeatureFlagEnabled ? 2 : undefined}>
				{isNewProfileFeatureFlagEnabled && showAlert ? (
					<AlertInfo.Root severity={typeMessage}>
						<Typography fontSize={'1rem'} fontWeight={600}>
							{message[typeMessage]}
						</Typography>
					</AlertInfo.Root>
				) : null}

				{isNewProfileFeatureFlagEnabled && !showAlert && currentEmailIsInvalid ? (
					<AlertInfo.Root severity="warning">
						<AlertInfo.Description>{contact.invalidEmail}</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}

				{SHOW_INFO_ALERT ? (
					<AlertInfo.Root
						severity="info"
						sx={{width: isNewProfileFeatureFlagEnabled ? '100%' : undefined}}>
						{contact.alertEmail}
					</AlertInfo.Root>
				) : null}

				{isNewProfileFeatureFlagEnabled ? (
					<TextField
						{...register('email')}
						label={contact.email}
						placeholder={contact.emailPlaceholder}
						error={!!errors.email || abandonError}
						helperText={handleHelperEmailText()}
					/>
				) : null}

				{!isNewProfileFeatureFlagEnabled ? (
					<Stack spacing={2} flex={1}>
						<AlertInfo.Root severity="info">{contact.alertEmail}</AlertInfo.Root>
						<TextField
							{...register('email')}
							label={contact.email}
							placeholder={contact.emailPlaceholder}
							error={!!errors.email || abandonError}
							helperText={handleHelperEmailText()}
						/>
					</Stack>
				) : null}

				<Stack direction="row" flex={1} justifyContent="flex-end" spacing={2}>
					<Button size="large" variant="outlined" onClick={handleBack}>
						{cancel}
					</Button>
					<Button disabled={loading} size="large" onClick={handleSave}>
						<Stack gap={1} direction="row">
							{save}
							{loading ? <Loading size={18} color="inherit" /> : null}
						</Stack>
					</Button>
				</Stack>
			</Stack>

			<DiscardChangesModal
				open={isDiscardChangesModalOpenState}
				setOpen={setIsDiscardChangesModalOpenState}
				onConfirm={onCancel}
			/>
		</FormProvider>
	);
};

export {EmailForm};
